/**
 * Puerta de los documentos legales del Guru (/terms-guru y /mentor-agreement).
 *
 * Estos dos documentos rigen la relación Gadamax LLC ↔ Guru y no forman parte
 * de lo que un usuario maneja en la plataforma. Tenían `noindex,nofollow`, que
 * los saca de Google pero no impide que cualquiera con el URL los abra.
 *
 * No alcanza con exigir sesión: un guru invitado tiene que poder LEERLOS antes
 * de registrarse — el checkbox de /register/guru enlaza a /terms-guru y en ese
 * momento todavía no hay cuenta. Pedirle que acepte un documento que no puede
 * abrir es justamente lo que vuelve discutible un click-wrap.
 *
 * Entonces pasa quien cumpla una de dos:
 *   - tiene sesión iniciada;
 *   - viene del registro con un token de invitación que ya validó el servidor;
 *   - o viene de recorrer /invitacion, la pieza de invitación al guru, que
 *     enlaza estos documentos a propósito (ver lib/guru-invite-flag.ts).
 *
 * La marca de sessionStorage no es un control de seguridad — se puede escribir
 * a mano desde la consola. Tampoco pretende serlo: el contenido no es secreto,
 * es un contrato que todo guru termina viendo. Lo que evita es que la URL
 * circule y que un usuario cualquiera aterrice en un documento que no le
 * corresponde.
 */
import { Link, useLocation } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import { useSession } from '@/lib/auth';
import { Logo } from '@/components/ui/logo';
import { Button } from '@/components/ui/button';
import { hasGuruInvitePreview, hasValidatedGuruInvite } from '@/lib/guru-invite-flag';

export function GuruLegalGate({ children }: { children: React.ReactNode }) {
  const { data: session, isLoading } = useSession();
  const location = useLocation();

  // Mientras resuelve la sesión no mostramos ni el documento ni el rechazo:
  // rebotar a alguien que sí tiene sesión sería peor que esperar un instante.
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-accent border-t-transparent" />
      </div>
    );
  }

  if (session || hasValidatedGuruInvite() || hasGuruInvitePreview()) {
    return <>{children}</>;
  }

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-[#DCE7EC]">
        <div className="mx-auto flex h-14 max-w-3xl items-center px-6">
          <Link to="/">
            <Logo variant="horizontal" size={28} />
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-lg px-6 py-24 text-center">
        <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-brand-primary text-white">
          <ShieldCheck className="h-6 w-6" />
        </span>
        <h1 className="mt-5 font-display text-2xl font-bold text-[#10232B]">
          Documento privado
        </h1>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
          Este documento rige la relación entre Gurumba y sus Gurus, y solo pueden
          consultarlo los Gurus de la plataforma. Si eres Guru, inicia sesión y lo
          encuentras en Mi cuenta, en la sección Legal.
        </p>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
          Si recibiste una invitación para ser Guru, ábrela desde el enlace del correo:
          desde ahí puedes leer los documentos antes de aceptar.
        </p>

        <div className="mt-8 flex items-center justify-center gap-3">
          <Button asChild>
            <Link to="/login" state={{ from: location.pathname }}>
              Iniciar sesión
            </Link>
          </Button>
          <Button variant="ghost" asChild>
            <Link to="/">Ir al inicio</Link>
          </Button>
        </div>

        <p className="mt-10 text-xs text-muted-foreground">
          ¿Dudas? Escríbenos a{' '}
          <a href="mailto:soporte@gurumba.com" className="text-brand-accent hover:underline">
            soporte@gurumba.com
          </a>
          .
        </p>
      </main>
    </div>
  );
}
